import { Input } from '@frontend/shadcn/components/ui/input';
import { Separator } from '@frontend/shadcn/components/ui/separator';
import { PlusIcon, MinusIcon, CircleInfoIcon, BoxCheckIcon } from '@revfluence/fresh-icons/solid/esm';
import React, { useEffect, useMemo } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@frontend/shadcn/components/ui/accordion';
import { RichTextEditor } from '@frontend/app/refresh-components';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Alert, AlertDescription } from '@frontend/shadcn/components/ui/alert';
import { useParams } from 'react-router-dom';
import { useAuth } from '@frontend/context/authContext';
import { useGetCatalogs } from '@frontend/app/containers/Settings/ProductFulfillment/Catalogs/hooks/useGetCatalogs';
import { useGetProjectConfigByType } from '@frontend/applications/ProductFulfillmentApp/hooks/useGetProjectConfigByType';
import { ProjectConfigType } from '@frontend/applications/ProductFulfillmentApp/types/globalTypes';
import { useGetSelectionCriteria } from '@frontend/app/containers/Settings/ProductFulfillment/Catalogs/hooks/useGetSelectionCriteria';
import { useAppContext } from '@frontend/context/AppContext';
import { Select, SelectContent, SelectValue, SelectItem, SelectTrigger } from '@frontend/shadcn/components/ui/select';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { useGetCatalogPreviewIframeUrl } from '@frontend/app/containers/Settings/ProductFulfillment/Catalogs/hooks/useGetCatalogPreviewIframeUrl';
import { useGetResources } from '@frontend/app/hooks';
import { useRecruitment, useProductPage } from '../RecruitmentContext';

export const ProductPage = () => {
  const {
    setProductTitle,
    setProductDescription,
    setProductHasDescription,
    setProductPrimaryButtonLabel,
    setProductCatalogId,
    setProductSelectionRuleId,
    setProductIframeUrl,
  } = useRecruitment();

  const { projectId } = useParams<{ projectId: string }>();

  const { clientInfo } = useAuth();

  const productPage = useProductPage();

  const { pfaApolloClient } = useAppContext();

  const { catalogs, loading: isCatalogsLoading } = useGetCatalogs({
    client: pfaApolloClient,
  });
  const { projectConfig } = useGetProjectConfigByType({
    variables: {
      projectId: Number(projectId),
      type: ProjectConfigType.ProductCatalog,
    },
    skip: !projectId,
    client: pfaApolloClient,
  });

  const filteredCatalogs = useMemo(() => catalogs.filter((catalog) =>
    projectConfig?.brandCatalogs?.includes(catalog.id)), [catalogs, projectConfig?.brandCatalogs]);

  const { criteria, loading: isCriteriaLoading } = useGetSelectionCriteria({
    variables: {
      brandCatalogId: productPage?.catalogId,
    },
    skip: !productPage?.catalogId,
    client: pfaApolloClient,
  });

  const {
    activeResources,
  } = useGetResources({
    variables: {
      resourceType: 'shopify',
      ignoreRequestContextUserId: true,
    },
  });

  const { catalogPreviewIframeUrl } = useGetCatalogPreviewIframeUrl({
    variables: {
      id: productPage?.catalogId,
      clientId: clientInfo?.id,
      isApplicationPage: true,
      programId: Number(projectId),
      resourceId: activeResources?.[0]?.id,
      selectionRuleId: productPage?.selectionRuleId,
    },
    skip: !productPage?.catalogId || !clientInfo?.id || !projectId || !activeResources?.[0]?.id,
    client: pfaApolloClient,
  });

  useEffect(() => {
    if (catalogPreviewIframeUrl) {
      setProductIframeUrl(catalogPreviewIframeUrl);
    }
  }, [catalogPreviewIframeUrl, setProductIframeUrl]);

  if (!productPage) {
    return null;
  }

  const {
    title,
    description,
    hasDescription,
    primaryButtonLabel,
    catalogId,
    selectionRuleId,
  } = productPage;

  return (
    <Accordion type="multiple" className="[&_h3]:mb-0" defaultValue={['hero']}>
      <AccordionItem value="hero" className="border-b-0">
        <AccordionTrigger>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-yellow-5 rounded-lg text-primary-foreground flex items-center justify-center">
              <BoxCheckIcon fontSize={12} />
            </div>
            <span className="text-base font-medium">Product</span>
          </div>
        </AccordionTrigger>
        <AccordionContent className="pb-0">
          <Alert>
            <AlertDescription>
              <div className="flex items-center gap-1">
                <div className="pr-1">
                  <CircleInfoIcon fontSize={16} />
                </div>
                <span className="font-medium">
                  Changes to the Product Catalog can be applied in
                  {' '}
                  <a
                    className="underline text-foreground hover:text-foreground hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                    href={`${window.location.origin}/client/${clientInfo?.id}/projects/${projectId}/settings/product_catalog`}
                  >
                    Products Settings.
                  </a>
                </span>
              </div>
            </AlertDescription>
          </Alert>

          {/* Title Section */}
          <div className="flex flex-col gap-2 mt-2">
            <label htmlFor="title" className="text-xs font-medium text-foreground">Title <span className="text-primaryMuted font-normal">(Required)</span></label>
            <div className="px-[1px]">
              <Input id="title" placeholder="Request Product" value={title} onChange={(e) => setProductTitle(e.target.value)} />
            </div>
          </div>

          {/* Description Section */}
          <div className="mt-2">
            {hasDescription && (
              <div className="flex flex-col items-start gap-2">
                <Button
                  variant="link"
                  className="gap-2 pl-0"
                  onClick={() => setProductHasDescription(false)}
                  size="xs"
                >
                  <MinusIcon />
                  Remove Description
                </Button>
                <RichTextEditor
                  placeholder="Upon acceptance, your product request will be reviewed before shipping."
                  value={description}
                  onChange={setProductDescription}
                  className="[&_.ql-editor]:min-h-[100px]"
                />
              </div>
            )}
            {!hasDescription && (
              <Button
                variant="link"
                className="gap-2 pl-0"
                onClick={() => setProductHasDescription(true)}
                size="xs"
              >
                <PlusIcon />
                Add Description
              </Button>
            )}
          </div>

          <Separator className="my-4" />

          {/* Catalog Section */}
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <label htmlFor="catalog" className="text-xs font-medium text-foreground">Catalog <span className="text-primaryMuted">(Required)</span></label>
              {isCatalogsLoading ? (
                <Skeleton className="w-full h-8" />
              ) : (
                <div className="px-[1px]">
                  <Select
                    value={catalogId?.toString()}
                    onValueChange={(value) => {
                      setProductCatalogId(Number(value));
                      setProductSelectionRuleId(null);
                      setProductIframeUrl(null);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a catalog" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredCatalogs.map((catalog) => (
                        <SelectItem key={catalog.id} value={catalog.id.toString()}>{catalog.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            {catalogId && (
              <div className="flex flex-col gap-2">
                <label htmlFor="selectionRule" className="text-xs font-medium text-foreground">Selection Rule</label>
                {isCriteriaLoading ? (
                  <Skeleton className="w-full h-8" />
                ) : (
                  <div className="px-[1px]">
                    <Select
                      value={selectionRuleId?.toString()}
                      onValueChange={(value) => {
                        setProductSelectionRuleId(Number(value));
                        setProductIframeUrl(null);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a cart rule" />
                      </SelectTrigger>
                      <SelectContent>
                        {criteria.map((criterion) => (
                          <SelectItem key={criterion.id} value={criterion.id.toString()}>{criterion.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}
          </div>

          <Separator className="my-4" />

          {/* Primary Button Label Section */}
          <div>
            <label htmlFor="buttonLabel" className="text-xs font-medium text-foreground">Primary Button Label <span className="text-primaryMuted">(Required)</span></label>
            <div className="px-[1px] pb-[1px]">
              <Input id="buttonLabel" placeholder="Final Step" className="mt-2" value={primaryButtonLabel} onChange={(e) => setProductPrimaryButtonLabel(e.target.value)} />
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
